import { Box, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { CustomTableSortLabel } from '@components';
import { Column } from '../../../../components/ImmutableDataTable/ImmutableDataTable';
import { formatDate } from '@utils/common';
import TableFilterDropdown from '../../../../components/TableFilterDropdown';
import {
  PENDING_SIGNUP_STATUS_LABELS,
  PENDING_SIGNUP_STATUS_FILTER_OPTIONS,
  FEEDBACK_SUBMITTED_FILTER_OPTIONS,
} from '../constants';
import type { PendingSignupTableRow } from '../hooks/usePendingSignupsTable';
import type { PendingSignupStatus } from '@api/pendingSignups';

const cellTextSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '1.25rem',
  textAlign: 'left',
};

const columnStyles = {
  customHeaderStyles: { textAlign: 'left', whiteSpace: 'nowrap', fontSize: '0.875rem' },
  customCellStyles: { padding: '0.5rem 0.5rem' },
};

type PendingSignupsTableColumnOptions = {
  sort: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (sortKey: string) => void;
  statusFilter: string;
  onStatusFilterChange: (filter: string) => void;
  feedbackSubmittedFilter: string;
  onFeedbackSubmittedFilterChange: (filter: string) => void;
};

export const getPendingSignupsTableColumns = ({
  sort,
  sortOrder,
  onSort,
  statusFilter,
  onStatusFilterChange,
  feedbackSubmittedFilter,
  onFeedbackSubmittedFilterChange,
}: PendingSignupsTableColumnOptions): Column<PendingSignupTableRow>[] => [
  {
    key: 'email',
    label: 'Email',
    ...columnStyles,
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, ...columnStyles.customCellStyles, minWidth: '200px' }}>
        {row.email || '-'}
      </Typography>
    ),
  },
  {
    key: 'username',
    label: 'Name',
    ...columnStyles,
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, ...columnStyles.customCellStyles, minWidth: '150px' }}>
        {row.username || '-'}
      </Typography>
    ),
  },
  {
    key: 'intendedCompanyName',
    label: 'Users Company',
    ...columnStyles,
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, ...columnStyles.customCellStyles, minWidth: '150px' }}>
        {row.intendedCompanyName || '-'}
      </Typography>
    ),
  },
  {
    key: 'matchedCompanyName',
    label: 'Matched Company',
    ...columnStyles,
    render: (_value, row) => {
      const company = row.matchedCompanyName;
      const clientCode = row.matchedClientCode;
      if (!company && !clientCode) {
        return (
          <Typography sx={{ ...cellTextSx, ...columnStyles.customCellStyles, minWidth: '150px' }}>
            -
          </Typography>
        );
      }
      return (
        <Box sx={{ minWidth: '150px' }}>
          <Typography sx={cellTextSx}>{company || '-'}</Typography>
        </Box>
      );
    },
  },
  {
    key: 'status',
    label: 'Status',
    ...columnStyles,
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.25rem',
          whiteSpace: 'nowrap',
        }}
      >
        Status
        <TableFilterDropdown
          label="Status"
          options={PENDING_SIGNUP_STATUS_FILTER_OPTIONS}
          showSingleSelectIcon
          selected={statusFilter ? [statusFilter] : []}
          setSelected={(filters) =>
            onStatusFilterChange(filters.length ? filters[filters.length - 1] : '')
          }
        />
      </Box>
    ),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, ...columnStyles.customCellStyles, minWidth: '130px' }}>
        {PENDING_SIGNUP_STATUS_LABELS[row.status as PendingSignupStatus] ?? row.status ?? '-'}
      </Typography>
    ),
  },
  {
    key: 'joiningDate',
    label: 'Joining Date',
    ...columnStyles,
    renderHeader: () => (
      <Box sx={{ minWidth: '100%' }}>
        <CustomTableSortLabel
          label="Joining Date"
          active={sort === 'joiningDate'}
          sortDirection={sort === 'joiningDate' ? (sortOrder === 'ASC' ? 'ASC' : 'DESC') : 'ASC'}
          onSort={() => onSort('joiningDate')}
          labelStyles={{ ...columnStyles.customHeaderStyles }}
        />
      </Box>
    ),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, ...columnStyles.customCellStyles, minWidth: '120px' }}>
        {row.joiningDate ? formatDate(row.joiningDate) : '-'}
      </Typography>
    ),
  },
  {
    key: 'feedbackSubmitted',
    label: 'New company request',
    ...columnStyles,
    renderHeader: () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
          whiteSpace: 'wrap',
          width: '150px',
        }}
      >
        New company request
        <TableFilterDropdown
          label="New company request"
          options={FEEDBACK_SUBMITTED_FILTER_OPTIONS}
          showSingleSelectIcon
          selected={feedbackSubmittedFilter ? [feedbackSubmittedFilter] : []}
          setSelected={(filters) =>
            onFeedbackSubmittedFilterChange(filters.length ? filters[filters.length - 1] : '')
          }
        />
      </Box>
    ),
    render: (_value, row) => (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '100px' }}
      >
        {row.feedbackSubmitted ? (
          <CheckCircleRoundedIcon sx={{ fontSize: '1.25rem', color: '#2F736E' }} />
        ) : (
          <Typography sx={{ ...cellTextSx, ...columnStyles.customCellStyles }}>-</Typography>
        )}
      </Box>
    ),
  },
];
